import { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';

const IOS_DISMISSED_KEY = 'vecindus_pwa_ios_dismissed';
const DESKTOP_DISMISSED_KEY = 'vecindus_pwa_desktop_dismissed';

function isCoarsePointer(): boolean {
  try { return window.matchMedia?.('(pointer: coarse)').matches ?? false; } catch { return false; }
}

/**
 * Install-surface localization (Jaime 2026-09-23 follow-up): the app has NO
 * global i18n layer (audit 2026-09-23), so these surfaces carry a minimal
 * self-contained es/en dictionary keyed off navigator.language. Default is
 * Spanish; any `en*` device language gets English. Contained to this file on
 * purpose — do not introduce a global i18n dependency in this pass.
 */
type InstallCopy = {
  dialogLabel: string;
  close: string;
  titleIphone: string;
  step1Share: string;
  step2More: string;
  addLabel: string;
  tapSharePre: string;
  tapShare: string;
  tapShareBar: string;
  tapShareBarPost: string;
  choose: string;
  andTap: string;
  chooseAdd: string;
  tap: string;
  tapAdd: string;
  addHomeMsg: string;
  gotIt: string;
  desktopBannerPre: string;
  desktopBannerPost: string;
  install: string;
  installApp: string;
};

const ES: InstallCopy = {
  dialogLabel: 'Instalar la app Vecindus',
  close: 'Cerrar',
  titleIphone: 'Instala Vecindus en tu iPhone',
  step1Share: 'Toca el botón Compartir (□↑).',
  step2More: 'Elige “Ver más”.',
  addLabel: 'Agregar a pantalla de inicio',
  tapSharePre: 'Toca el icono',
  tapShare: 'Compartir',
  tapShareBar: 'Compartir',
  tapShareBarPost: 'en la barra de Safari.',
  choose: 'Elige',
  andTap: 'y toca',
  chooseAdd: 'Agregar a pantalla de inicio',
  tap: 'Toca',
  tapAdd: 'Agregar',
  addHomeMsg: '. La app aparecerá en tu pantalla de inicio.',
  gotIt: 'Entendido',
  desktopBannerPre: 'Instala',
  desktopBannerPost: 'en tu equipo para acceder más rápido.',
  install: 'Instalar',
  installApp: 'Instalar app',
};

const EN: InstallCopy = {
  dialogLabel: 'Install the Vecindus app',
  close: 'Close',
  titleIphone: 'Install Vecindus on your iPhone',
  step1Share: 'Tap the Share button.',
  step2More: 'Choose “View more”.',
  addLabel: 'Add to Home Screen',
  tapSharePre: 'Tap the',
  tapShare: 'Share',
  tapShareBar: 'Share',
  tapShareBarPost: 'in the Safari toolbar.',
  choose: 'Choose',
  andTap: 'and tap',
  chooseAdd: 'Add to Home Screen',
  tap: 'Tap',
  tapAdd: 'Add',
  addHomeMsg: '. The app will appear on your home screen.',
  gotIt: 'Got it',
  desktopBannerPre: 'Install',
  desktopBannerPost: 'on your computer for faster access.',
  install: 'Install',
  installApp: 'Install app',
};

function detectInstallLang(): 'es' | 'en' {
  try {
    const langs = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean) as string[];
    return langs.some((l) => l.toLowerCase().startsWith('en')) ? 'en' : 'es';
  } catch { return 'es'; }
}

function getCopy(): InstallCopy {
  return detectInstallLang() === 'en' ? EN : ES;
}

/**
 * PWA install entry point (2026-09-16, Jaime-approved task).
 *
 * Browser-aware update (Jaime 2026-09-23): install flows differ per browser.
 * - Android/Chrome + desktop Chromium (beforeinstallprompt available): header
 *   icon button that calls prompt() once. Desktop also gets a mild dismissible
 *   banner ("Instala Vecindus"); on mobile the header button alone avoids
 *   covering content.
 * - iOS Safari (no beforeinstallprompt): one-time guided overlay — Compartir →
 *   "Agregar a pantalla de inicio".
 * - iOS Chrome/Edge/Firefox (iOS 16.4+ allows PWA install from any browser):
 *   overlay gives the real share-sheet steps (Compartir → "Ver más" →
 *   "Agregar a pantalla de inicio"), corrected per Jaime 2026-09-23.
 * - Users already running standalone are never prompted.
 */
export function InstallPrompt() {
  const { installed, canInstall, isIos, iosBrowser, promptInstall } = usePwaInstall();
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [showDesktopBanner, setShowDesktopBanner] = useState(false);
  const [t] = useState<InstallCopy>(() => getCopy());

  useEffect(() => {
    if (installed || !isIos || canInstall) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(IOS_DISMISSED_KEY) === '1'; } catch { /* private mode */ }
    if (dismissed) return;
    // Small delay so the overlay never fights the login transition.
    const t2 = window.setTimeout(() => setShowIosGuide(true), 1500);
    return () => window.clearTimeout(t2);
  }, [installed, isIos, canInstall]);

  // Desktop-only mild banner (Jaime 2026-09-23): Chromium desktop fires
  // beforeinstallprompt; Safari/Firefox desktop don't, so the banner hides
  // there automatically. Touch devices use the header button instead.
  useEffect(() => {
    if (installed || !canInstall || isIos || isCoarsePointer()) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(DESKTOP_DISMISSED_KEY) === '1'; } catch { /* private mode */ }
    if (dismissed) return;
    const t2 = window.setTimeout(() => setShowDesktopBanner(true), 2500);
    return () => window.clearTimeout(t2);
  }, [installed, canInstall, isIos]);

  if (installed) return null;

  const dismissIos = () => {
    setShowIosGuide(false);
    try { localStorage.setItem(IOS_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  };

  const dismissDesktop = () => {
    setShowDesktopBanner(false);
    try { localStorage.setItem(DESKTOP_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  };

  const iosOverlay = showIosGuide ? (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      onClick={dismissIos}
      role="dialog"
      aria-modal="true"
      aria-label={t.dialogLabel}
    >
      <div
        className="relative bg-[var(--v-surface-1)] rounded-2xl shadow-xl w-full max-w-sm p-6 border border-[var(--v-border-light)] "
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismissIos}
          aria-label={t.close}
          className="absolute top-3 right-3 p-1.5 rounded-full text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {iosBrowser !== 'safari' ? (
          // iOS Chrome/Edge/Firefox (Jaime 2026-09-23 correction): iOS 16.4+
          // installs PWAs from any browser via its own menu — no Safari detour.
          <>
            <h2 className="text-lg font-bold text-[var(--v-text-primary)] mb-3 pr-8">{t.titleIphone}</h2>
            <ol className="space-y-3 text-sm text-[var(--v-text-primary)]">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700)] flex items-center justify-center text-xs font-bold">1</span>
                <span>{t.step1Share}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700)] flex items-center justify-center text-xs font-bold">2</span>
                <span>{t.step2More}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700)] flex items-center justify-center text-xs font-bold">3</span>
                <span>{t.tap} <strong>{t.addLabel}</strong>{t.addHomeMsg}</span>
              </li>
            </ol>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-[var(--v-text-primary)] mb-3 pr-8">{t.titleIphone}</h2>
            <ol className="space-y-3 text-sm text-[var(--v-text-primary)]">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700)] flex items-center justify-center text-xs font-bold">1</span>
                <span className="flex items-center gap-1.5 flex-wrap">
                  {t.tapSharePre} <Share className="inline w-4 h-4 text-[var(--v-teal-700)]" /> <strong>{t.tapShareBar}</strong> {t.tapShareBarPost}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700)] flex items-center justify-center text-xs font-bold">2</span>
                <span>{t.choose} <strong>“{t.chooseAdd}”</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[var(--v-teal-50,#e6f7f5)] text-[var(--v-teal-700)] flex items-center justify-center text-xs font-bold">3</span>
                <span>{t.tap} <strong>{t.tapAdd}</strong>{t.addHomeMsg}</span>
              </li>
            </ol>
          </>
        )}

        <button
          onClick={dismissIos}
          className="mt-5 w-full py-2.5 rounded-full bg-[var(--v-teal-700)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          {t.gotIt}
        </button>
      </div>
    </div>
  ) : null;

  // Desktop: mild dismissible banner (bottom-right), no content blocker.
  const desktopBanner = showDesktopBanner && canInstall && !isIos ? (
    <div
      className="fixed bottom-4 right-4 z-[90] flex items-center gap-3 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-xl shadow-lg px-4 py-3 max-w-sm"
      role="status"
      data-testid="pwa-desktop-banner"
    >
      <span className="text-sm text-[var(--v-text-primary)]">{t.desktopBannerPre} <strong>Vecindus</strong> {t.desktopBannerPost}</span>
      <button
        onClick={() => { void promptInstall(); }}
        className="shrink-0 px-3 py-1.5 rounded-full bg-[var(--v-teal-700)] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
      >
        {t.install}
      </button>
      <button
        onClick={dismissDesktop}
        aria-label={t.close}
        className="shrink-0 p-1 rounded-full text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  ) : null;

  return (
    <>
      {canInstall && (
        <button
          onClick={() => { void promptInstall(); }}
          title={t.installApp}
          aria-label={t.installApp}
          data-testid="pwa-install-button"
          className="p-2 text-[var(--v-text-secondary)] hover:bg-[var(--v-teal-50,#e6f7f5)] hover:text-[var(--v-teal-700)] rounded-full transition-colors"
        >
          <Download className="w-5 h-5" />
        </button>
      )}
      {iosOverlay}
      {desktopBanner}
    </>
  );
}
